import React from 'react';
import { MODAL_TYPES } from '@components/modals';
import { CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import { PlaceOfServiceSelect } from '@features/billing/components/Claim/PlaceOfServiceSelect';
import { useJotMeetAvailability, useTelehealthAccess } from '@features/telehealth';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  type SxProps,
  TextField,
  type Theme,
  Typography,
} from '@mui/material';
import { useModal } from '@store/hooks';
import {
  type ConcreteVisitModality,
  getVisitModalityLabel,
  isTelehealthModality,
  normalizeCptCodes,
  VISIT_MODALITY_OPTIONS,
  type VisitModality,
} from '@utils/modalityPlaceOfService';

interface SessionDetailsFieldsProps {
  modality: VisitModality;
  placeOfService?: string | null;
  cptCodes?: string[] | null;
  useJotmeet?: boolean | null;
  error?: string | null;
  disabled?: boolean;
  disableBillingFields?: boolean;
  compact?: boolean;
  onModalityChange: (modality: ConcreteVisitModality | null) => void;
  onPlaceOfServiceChange: (placeOfService: string | null) => void;
  onCptCodesChange: (cptCodes: string[]) => void;
  onUseJotmeetChange?: (useJotmeet: boolean) => void;
}

const compactFieldLabelSx: SxProps<Theme> = {
  display: 'block',
  mb: 0.5,
  color: 'text.secondary',
  fontSize: '0.75rem',
  fontWeight: 600,
  lineHeight: 1.2,
};

const compactSelectSx: SxProps<Theme> = {
  minWidth: '100%',
  '& .MuiOutlinedInput-root': {
    height: 40,
    minHeight: 40,
    alignItems: 'center',
  },
  '& .MuiInputBase-input': {
    padding: '8.5px 14px',
    paddingRight: '32px',
    fontSize: '0.875rem',
    lineHeight: '1.4375em',
    textAlign: 'left',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSelect-icon': {
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
  },
};

const compactCptFieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    height: 40,
    minHeight: 40,
    alignItems: 'center',
    py: 0,
  },
  '& .MuiOutlinedInput-input': {
    padding: '8.5px 14px',
    fontSize: '0.875rem',
    lineHeight: '1.4375em',
  },
};

const compactDropdownSx: SxProps<Theme> = {
  zIndex: 1600,
};

const compactSelectMenuProps = {
  variant: 'menu',
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  marginThreshold: 8,
  sx: compactDropdownSx,
  slotProps: {
    paper: {
      sx: compactDropdownSx,
    },
  },
} as const;

const compactSelectSlotProps = {
  select: {
    MenuProps: {
      ...compactSelectMenuProps,
    },
  },
};

type CompactSelectId = 'modality' | 'placeOfService';

export const SessionDetailsFields: React.FC<SessionDetailsFieldsProps> = ({
  modality,
  placeOfService = null,
  cptCodes = [],
  useJotmeet = false,
  error,
  disabled = false,
  disableBillingFields = false,
  compact = false,
  onModalityChange,
  onPlaceOfServiceChange,
  onCptCodesChange,
  onUseJotmeetChange,
}) => {
  const { open: openModal } = useModal();
  const { hasAccess: hasTelehealthAccess, isLoading: isTelehealthAccessLoading } =
    useTelehealthAccess();
  const { isAvailable: isJotMeetAvailable, isLoading: isJotMeetAvailabilityLoading } =
    useJotMeetAvailability();
  const canUseJotMeet = hasTelehealthAccess && modality === 'video';
  const canUpsellJotMeet =
    !hasTelehealthAccess &&
    !isTelehealthAccessLoading &&
    !isJotMeetAvailabilityLoading &&
    isJotMeetAvailable &&
    modality === 'video';
  const [openCompactSelect, setOpenCompactSelect] = React.useState<CompactSelectId | null>(null);

  const getCompactSelectSlotProps = (selectId: CompactSelectId) => ({
    select: {
      ...compactSelectSlotProps.select,
      open: openCompactSelect === selectId,
      onOpen: () => setOpenCompactSelect(selectId),
      onClose: () => setOpenCompactSelect(current => (current === selectId ? null : current)),
    },
  });

  const handleCompactSelectOpen = (selectId: CompactSelectId) => (event: React.SyntheticEvent) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[role="combobox"]')) return;

    event.stopPropagation();
    setOpenCompactSelect(selectId);
  };

  return (
    <Stack
      spacing={compact ? 1.25 : 1.75}
      sx={compact ? { p: 1.25, width: '100%', minWidth: 0 } : undefined}
      onClick={event => event.stopPropagation()}
    >
      {error ? (
        <Alert severity="error" sx={{ py: compact ? 0 : undefined }}>
          {error}
        </Alert>
      ) : null}

      <Stack direction={compact ? 'column' : { xs: 'column', sm: 'row' }} spacing={1.25}>
        <Box
          sx={{ flex: 1, minWidth: 0 }}
          onClickCapture={compact ? handleCompactSelectOpen('modality') : undefined}
          onFocusCapture={compact ? handleCompactSelectOpen('modality') : undefined}
          onMouseDownCapture={compact ? handleCompactSelectOpen('modality') : undefined}
        >
          {compact ? <Typography sx={compactFieldLabelSx}>Visit mode</Typography> : null}
          <TextField
            select
            fullWidth
            size="small"
            label={compact ? undefined : 'Visit mode'}
            value={modality ?? ''}
            disabled={disabled}
            onChange={event => {
              const value = event.target.value as ConcreteVisitModality | '';
              setOpenCompactSelect(null);
              onModalityChange(value || null);
            }}
            slotProps={
              compact ? getCompactSelectSlotProps('modality') : { inputLabel: { shrink: true } }
            }
            sx={compact ? compactSelectSx : undefined}
          >
            <MenuItem value="">
              <em>Not set</em>
            </MenuItem>
            {VISIT_MODALITY_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box
          sx={{ flex: 1, minWidth: 0 }}
          onClickCapture={compact ? handleCompactSelectOpen('placeOfService') : undefined}
          onFocusCapture={compact ? handleCompactSelectOpen('placeOfService') : undefined}
          onMouseDownCapture={compact ? handleCompactSelectOpen('placeOfService') : undefined}
        >
          {compact ? <Typography sx={compactFieldLabelSx}>Place of service</Typography> : null}
          <PlaceOfServiceSelect
            fullWidth
            label={compact ? undefined : 'Place of service'}
            emptyLabel={compact ? 'No POS selected' : undefined}
            value={placeOfService ?? ''}
            disabled={disabled || disableBillingFields}
            onChange={event => {
              setOpenCompactSelect(null);
              onPlaceOfServiceChange(event.target.value || null);
            }}
            slotProps={
              compact
                ? getCompactSelectSlotProps('placeOfService')
                : { inputLabel: { shrink: true } }
            }
            sx={
              compact
                ? compactSelectSx
                : {
                    minWidth: '100%',
                    '& .MuiInputBase-input': {
                      padding: '8.5px 14px',
                      paddingRight: '32px',
                      fontSize: '0.875rem',
                      textAlign: 'left',
                    },
                    '& .MuiOutlinedInput-root': {
                      height: 'auto',
                    },
                  }
            }
          />
        </Box>
      </Stack>

      <Box>
        <Typography
          variant="caption"
          sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}
        >
          CPT codes
        </Typography>
        <CPTCodePicker
          value={normalizeCptCodes(cptCodes)}
          disabled={disabled || disableBillingFields}
          onChange={codes => onCptCodesChange(normalizeCptCodes(codes))}
          inputLabel={compact ? null : 'CPT code'}
          popperSx={compact ? compactDropdownSx : undefined}
          textFieldSx={compact ? compactCptFieldSx : undefined}
          showHeader={false}
          showHelperText={false}
          size="small"
        />
      </Box>

      {modality === 'video' && onUseJotmeetChange ? (
        canUseJotMeet ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(useJotmeet)}
                disabled={disabled}
                onChange={event => onUseJotmeetChange(event.target.checked)}
              />
            }
            label="Use JotMeet for this video visit"
            sx={{ mr: 0 }}
          />
        ) : canUpsellJotMeet ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              JotMeet can host video visits.
            </Typography>
            <Button
              size="small"
              onClick={() =>
                openModal(MODAL_TYPES.ADDON_UPSELL, {
                  addonId: 'jotmeet',
                  sx: { borderRadius: 3, overflow: 'hidden' },
                })
              }
            >
              Learn more
            </Button>
          </Stack>
        ) : null
      ) : isTelehealthModality(modality) ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {getVisitModalityLabel(modality)} visits are treated as telehealth for billing.
        </Typography>
      ) : null}
    </Stack>
  );
};

export default SessionDetailsFields;
